// flash.filters + com.gskinner.geom.ColorMatrix equivalents.
//
// Filters are realised as SVG filter primitives referenced from canvas2d
// (`ctx.filter = url(#id)`), which keeps them GPU-accelerated and matches
// flash's sRGB, non-linear compositing when color-interpolation-filters is
// pinned to sRGB.

const SVG_NS = "http://www.w3.org/2000/svg";

let defs: SVGDefsElement | null = null;
const registered = new Map<string, string>();
let uid = 0;

function ensureDefs(): SVGDefsElement {
  if (defs) return defs;
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("width", "0");
  svg.setAttribute("height", "0");
  svg.style.position = "absolute";
  svg.style.width = "0";
  svg.style.height = "0";
  svg.style.overflow = "hidden";
  defs = document.createElementNS(SVG_NS, "defs");
  svg.appendChild(defs);
  document.body.appendChild(svg);
  return defs;
}

/** Flash blurX/blurY are box-blur passes; ~2.3 maps one flash unit to one sigma. */
const BLUR_TO_SIGMA = 1 / 2.3;

export abstract class BitmapFilter {
  /** Extra room the filter needs around the source, in pixels. */
  abstract get padding(): number;

  /**
   * `url(#...)` string for ctx.filter. The SVG node is shared process-wide per
   * cacheKey, and the url string is kept on the filter so a filter that is
   * re-read every frame costs one key comparison.
   */
  get cssFilter(): string {
    const key = this.cacheKey;
    if (key === this.cssKey) return this.css;
    let id = registered.get(key);
    if (id === undefined) {
      id = `xf${uid++}`;
      const node = document.createElementNS(SVG_NS, "filter");
      node.id = id;
      node.setAttribute("color-interpolation-filters", "sRGB");
      node.setAttribute("x", "-50%");
      node.setAttribute("y", "-50%");
      node.setAttribute("width", "200%");
      node.setAttribute("height", "200%");
      node.innerHTML = this.markup();
      ensureDefs().appendChild(node);
      registered.set(key, id);
    }
    this.cssKey = key;
    this.css = `url(#${id})`;
    return this.css;
  }

  abstract clone(): BitmapFilter;

  /** Stable identity of the filter's parameters. */
  protected abstract get cacheKey(): string;
  /** SVG primitives implementing the filter, as markup. */
  protected abstract markup(): string;

  private css = "";
  private cssKey = "";
}

export class GlowFilter extends BitmapFilter {
  constructor(
    public color = 0xff0000,
    public alpha = 1,
    public blurX = 6,
    public blurY = 6,
    public strength = 2,
    public quality = 1,
    public inner = false,
    public knockout = false,
  ) {
    super();
  }

  get padding(): number {
    return Math.ceil(Math.max(this.blurX, this.blurY) * 1.5) + 4;
  }

  protected get cacheKey(): string {
    return `glow:${this.color}:${this.alpha}:${this.blurX}:${this.blurY}:${this.strength}:${this.inner}:${this.knockout}`;
  }

  protected markup(): string {
    const sx = (this.blurX * BLUR_TO_SIGMA).toFixed(3);
    const sy = (this.blurY * BLUR_TO_SIGMA).toFixed(3);
    const r = (this.color >> 16) & 0xff;
    const g = (this.color >> 8) & 0xff;
    const b = this.color & 0xff;
    const hex = `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
    // An inner glow grows inward from the edges, so it blurs the INVERTED
    // alpha: blurring SourceAlpha directly leaves the interior of a solid
    // shape at ~1, which amplifies to a flat flood of colour over the whole
    // silhouette instead of a rim.
    const source = this.inner ? "inv" : "SourceAlpha";
    const invert = this.inner
      ? `<feComponentTransfer in="SourceAlpha" result="inv">
           <feFuncA type="table" tableValues="1 0"/>
         </feComponentTransfer>`
      : "";
    // strength amplifies then clamps the blurred alpha, as flash does.
    const glow = `${invert}
      <feGaussianBlur in="${source}" stdDeviation="${sx} ${sy}" result="b"/>
      <feComponentTransfer in="b" result="amp">
        <feFuncA type="linear" slope="${this.strength}" intercept="0"/>
      </feComponentTransfer>
      <feFlood flood-color="${hex}" flood-opacity="${this.alpha}" result="c"/>
      <feComposite in="c" in2="amp" operator="in" result="glow"/>`;
    if (this.inner) {
      return `${glow}
      <feComposite in="glow" in2="SourceAlpha" operator="in" result="inner"/>
      <feMerge><feMergeNode in="SourceGraphic"/><feMergeNode in="inner"/></feMerge>`;
    }
    if (this.knockout)
      return `${glow}<feComposite in="glow" in2="SourceAlpha" operator="out"/>`;
    return `${glow}<feMerge><feMergeNode in="glow"/><feMergeNode in="SourceGraphic"/></feMerge>`;
  }

  clone(): GlowFilter {
    return new GlowFilter(
      this.color,
      this.alpha,
      this.blurX,
      this.blurY,
      this.strength,
      this.quality,
      this.inner,
      this.knockout,
    );
  }
}

export class ColorMatrixFilter extends BitmapFilter {
  matrix: number[];

  constructor(matrix?: IndexedNumbers | null) {
    super();
    this.matrix = fixMatrix(matrix).slice(0, 20);
  }

  get padding(): number {
    return 0;
  }

  protected get cacheKey(): string {
    return `cm:${this.matrix.join(",")}`;
  }

  protected markup(): string {
    // flash offsets are 0..255, svg wants 0..1 in the fifth column.
    const m = this.matrix;
    const v: number[] = [];
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 5; col++) {
        const value = m[row * 5 + col];
        v.push(col === 4 ? value / 255 : value);
      }
    }
    return `<feColorMatrix type="matrix" values="${v.map((n) => n.toFixed(6)).join(" ")}"/>`;
  }

  clone(): ColorMatrixFilter {
    return new ColorMatrixFilter(this.matrix.slice());
  }
}

const DELTA_INDEX = [
  0, 0.01, 0.02, 0.04, 0.05, 0.06, 0.07, 0.08, 0.1, 0.11, 0.12, 0.14, 0.15,
  0.16, 0.17, 0.18, 0.2, 0.21, 0.22, 0.24, 0.25, 0.27, 0.28, 0.3, 0.32, 0.34,
  0.36, 0.38, 0.4, 0.42, 0.44, 0.46, 0.48, 0.5, 0.53, 0.56, 0.59, 0.62, 0.65,
  0.68, 0.71, 0.74, 0.77, 0.8, 0.83, 0.86, 0.89, 0.92, 0.95, 0.98, 1, 1.06,
  1.12, 1.18, 1.24, 1.3, 1.36, 1.42, 1.48, 1.54, 1.6, 1.66, 1.72, 1.78, 1.84,
  1.9, 1.96, 2, 2.12, 2.25, 2.37, 2.5, 2.62, 2.75, 2.87, 3, 3.2, 3.4, 3.6, 3.8,
  4, 4.3, 4.7, 4.9, 5, 5.5, 6, 6.5, 6.8, 7, 7.3, 7.5, 7.8, 8, 8.4, 8.7, 9, 9.4,
  9.6, 9.8, 10,
];

const IDENTITY = [
  1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1,
];
const LENGTH = 25;

/**
 * com.gskinner.geom.ColorMatrix. Subclasses Array in AS3; here it exposes
 * indexed access plus `length` so it stays assignable to ColorMatrixFilter.
 */
export class ColorMatrix {
  [index: number]: number;
  length = LENGTH;

  constructor(matrix?: number[] | ColorMatrix) {
    const src = fixMatrix(matrix);
    for (let i = 0; i < LENGTH; i++) this[i] = src[i];
  }

  reset(): void {
    for (let i = 0; i < LENGTH; i++) this[i] = IDENTITY[i];
  }

  adjustColor(
    brightness: number,
    contrast: number,
    saturation: number,
    hue: number,
  ): void {
    this.adjustHue(hue);
    this.adjustContrast(contrast);
    this.adjustBrightness(brightness);
    this.adjustSaturation(saturation);
  }

  adjustBrightness(val: number): void {
    val = Math.min(100, Math.max(-100, val));
    if (val === 0 || isNaN(val)) return;
    this.multiplyMatrix([
      1,
      0,
      0,
      0,
      val,
      0,
      1,
      0,
      0,
      val,
      0,
      0,
      1,
      0,
      val,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      1,
    ]);
  }

  adjustContrast(val: number): void {
    val = Math.min(100, Math.max(-100, val));
    if (val === 0 || isNaN(val)) return;
    let x: number;
    if (val < 0) {
      x = 127 + (val / 100) * 127;
    } else {
      x = val % 1;
      x =
        x === 0
          ? DELTA_INDEX[val]
          : DELTA_INDEX[val << 0] * (1 - x) + DELTA_INDEX[(val << 0) + 1] * x;
      x = x * 127 + 127;
    }
    const o = 0.5 * (127 - x);
    this.multiplyMatrix([
      x / 127,
      0,
      0,
      0,
      o,
      0,
      x / 127,
      0,
      0,
      o,
      0,
      0,
      x / 127,
      0,
      o,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      1,
    ]);
  }

  adjustSaturation(val: number): void {
    val = Math.min(100, Math.max(-100, val));
    if (val === 0 || isNaN(val)) return;
    const x = 1 + (val > 0 ? (3 * val) / 100 : val / 100);
    const lumR = 0.3086;
    const lumG = 0.6094;
    const lumB = 0.082;
    this.multiplyMatrix([
      lumR * (1 - x) + x,
      lumG * (1 - x),
      lumB * (1 - x),
      0,
      0,
      lumR * (1 - x),
      lumG * (1 - x) + x,
      lumB * (1 - x),
      0,
      0,
      lumR * (1 - x),
      lumG * (1 - x),
      lumB * (1 - x) + x,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      1,
    ]);
  }

  adjustHue(val: number): void {
    val = (Math.min(180, Math.max(-180, val)) / 180) * Math.PI;
    if (val === 0 || isNaN(val)) return;
    const cosVal = Math.cos(val);
    const sinVal = Math.sin(val);
    const lumR = 0.213;
    const lumG = 0.715;
    const lumB = 0.072;
    this.multiplyMatrix([
      lumR + cosVal * (1 - lumR) + sinVal * -lumR,
      lumG + cosVal * -lumG + sinVal * -lumG,
      lumB + cosVal * -lumB + sinVal * (1 - lumB),
      0,
      0,
      lumR + cosVal * -lumR + sinVal * 0.143,
      lumG + cosVal * (1 - lumG) + sinVal * 0.14,
      lumB + cosVal * -lumB + sinVal * -0.283,
      0,
      0,
      lumR + cosVal * -lumR + sinVal * -(1 - lumR),
      lumG + cosVal * -lumG + sinVal * lumG,
      lumB + cosVal * (1 - lumB) + sinVal * lumB,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      0,
      1,
    ]);
  }

  concat(matrix: number[] | ColorMatrix): void {
    const m = fixMatrix(matrix);
    if (m.length !== LENGTH) return;
    this.multiplyMatrix(m);
  }

  clone(): ColorMatrix {
    return new ColorMatrix(this);
  }

  toArray(): number[] {
    const out: number[] = [];
    for (let i = 0; i < 20; i++) out.push(this[i]);
    return out;
  }

  protected multiplyMatrix(m: number[]): void {
    const col: number[] = [];
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) col[j] = this[j + i * 5];
      for (let j = 0; j < 5; j++) {
        let val = 0;
        for (let k = 0; k < 5; k++) val += m[j + k * 5] * col[k];
        this[j + i * 5] = val;
      }
    }
  }
}

/** Accepts either a plain array or a ColorMatrix; both are length-indexed. */
type IndexedNumbers = { readonly length: number; [index: number]: number };

function fixMatrix(matrix?: IndexedNumbers | null): number[] {
  if (!matrix) return IDENTITY.slice();
  const arr: number[] = [];
  for (let i = 0; i < matrix.length; i++) arr.push(matrix[i]);
  if (arr.length < LENGTH)
    return arr.concat(IDENTITY.slice(arr.length, LENGTH));
  if (arr.length > LENGTH) return arr.slice(0, LENGTH);
  return arr;
}
